import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button, Collapse, Empty, Input, Modal, Skeleton, Tag, message } from 'antd';
import * as AntDIcons from '@ant-design/icons';
import ResponsiveBreadcrumb from '../../../components/breadcrumb/ResponsiveBreadcrumb';
import DocumentViewer from '../../../components/document-viewer/DocumentViewer';
import Utils from '../../../misc/Utils';
import Enums from '../../../misc/Enums';
import { getCourses, getTopicsByCourse } from '../../../services/MicrocredentialsCourseTopicService';
import '../../../pages/common/BraineeModule.css';

const { Panel } = Collapse;
const ALLOWED_ROLES = ['super admin', 'admin', 'teacher', 'co-teacher'];

function getTeacherGuidePdfUrl(record) {
   const guideValue = record?.teacher_guide ?? record?.Teacher_guide ?? record?.teacherGuide ?? record?.TeacherGuide;
   if (!guideValue) return '';

   const value = String(guideValue).trim();
   if (!value) return '';
   if (/^https?:\/\//i.test(value)) return value;
   return `${process.env.REACT_APP_API_S3_URL}/${value}`;
}

export default function TeacherGuideCourses() {
   const navigate = useNavigate();
   const userRole = Utils.getCurrentUserInfo(Enums.UserInfo.role);
   const [courseGroups, setCourseGroups] = useState([]);
   const [loading, setLoading] = useState(false);
   const [search, setSearch] = useState('');
   const [preview, setPreview] = useState(null);

   useEffect(() => {
      Utils.verifyJwt();
      if (!ALLOWED_ROLES.includes(String(userRole || '').toLowerCase())) {
         navigate('/home');
         return;
      }

      let active = true;
      setLoading(true);
      getCourses()
         .then(async (data) => {
            const courses = Array.isArray(data) ? [...data] : [];
            courses.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            const topicResults = await Promise.allSettled(
               courses.map((course) => getTopicsByCourse(course.course_id ?? course.id))
            );
            if (!active) return;
            setCourseGroups(courses.map((course, index) => ({
               ...course,
               topics: topicResults[index].status === 'fulfilled' && Array.isArray(topicResults[index].value)
                  ? topicResults[index].value
                  : [],
            })));
            if (topicResults.some((result) => result.status === 'rejected')) {
               message.warning('Some course topics could not be loaded.');
            }
         })
         .catch(() => {
            if (active) {
               setCourseGroups([]);
               message.error("Unable to load the teacher's guide library.");
            }
         })
         .finally(() => active && setLoading(false));

      return () => { active = false; };
   }, [navigate, userRole]);

   const filteredGroups = useMemo(() => {
      const term = search.trim().toLowerCase();
      if (!term) return courseGroups;

      return courseGroups.reduce((groups, course) => {
         const courseMatches = `${course.name || ''} ${course.code || ''}`.toLowerCase().includes(term);
         const topics = courseMatches
            ? course.topics
            : course.topics.filter((topic) => `${topic.title || ''} ${topic.description || ''}`.toLowerCase().includes(term));
         if (courseMatches || topics.length > 0) groups.push({ ...course, topics });
         return groups;
      }, []);
   }, [courseGroups, search]);

   const guideCount = useMemo(() => courseGroups.reduce(
      (total, course) => total + course.topics.filter(getTeacherGuidePdfUrl).length,
      0
   ), [courseGroups]);

   const openGuide = (course, topic) => {
      const document = getTeacherGuidePdfUrl(topic);
      if (!document) return;
      setPreview({
         document,
         name: `${topic.title || 'Topic'} Teacher Guide.pdf`,
         title: topic.title || "Teacher's Guide",
         courseName: course.name || 'Course',
      });
   };

   const renderSkeletons = () => (
      <div className="brainee-module-skeleton-grid">
         {Array.from({ length: 4 }).map((_, index) => (
            <div className="brainee-module-card is-skeleton" key={index}>
               <Skeleton.Avatar active shape="square" />
               <div className="brainee-module-card-copy"><Skeleton.Input active size="small" /></div>
            </div>
         ))}
      </div>
   );

   return (
      <div className="dashboard-area-all brainee-module-page">
         <div className="dashboard-item brainee-module-shell">
            <div className="brainee-module-breadcrumb">
               <ResponsiveBreadcrumb items={[
                  { key: 'home', node: <Link to="/home">Home</Link> },
                  { key: 'microcredentials', node: 'Brainee Microcredentials' },
                  { key: 'teacher-guides', node: "Teacher's Guide" },
               ]} />
            </div>

            <section className="brainee-module-hero">
               <div>
                  <span className="brainee-module-kicker">Brainee Microcredentials</span>
                  <h1>Teacher&apos;s Guide Library</h1>
                  <p>Browse each course and open the guide prepared for a specific topic.</p>
                  <div className="brainee-module-meta">
                     <span><AntDIcons.BookOutlined /> {courseGroups.length} courses</span>
                     <span><AntDIcons.FilePdfOutlined /> {guideCount} topic guides</span>
                  </div>
               </div>
            </section>

            <section className="brainee-module-panel">
               <div className="brainee-module-panel-header">
                  <div>
                     <span className="brainee-module-kicker">Browse by course</span>
                     <h2>Course Groups</h2>
                  </div>
                  <Input
                     allowClear
                     prefix={<AntDIcons.SearchOutlined />}
                     placeholder="Search courses or topics"
                     value={search}
                     onChange={(event) => setSearch(event.target.value)}
                     style={{ maxWidth: 320 }}
                  />
               </div>

               {loading ? renderSkeletons() : filteredGroups.length > 0 ? (
                  <Collapse accordion className="teacher-guide-course-groups">
                     {filteredGroups.map((course) => {
                        const courseId = course.course_id ?? course.id;
                        const available = course.topics.filter(getTeacherGuidePdfUrl).length;
                        return (
                           <Panel
                              key={courseId}
                              header={(
                                 <span>
                                    <AntDIcons.BookOutlined />&nbsp; {course.name || `Course ${courseId}`}
                                    {course.code ? <Tag style={{ marginLeft: 10 }}>{course.code}</Tag> : null}
                                 </span>
                              )}
                              extra={<span className="brainee-module-count">{available}/{course.topics.length} guides</span>}
                           >
                              {course.topics.length > 0 ? (
                                 <div className="brainee-module-grid">
                                    {course.topics.map((topic) => {
                                       const hasGuide = Boolean(getTeacherGuidePdfUrl(topic));
                                       return (
                                          <button
                                             className={`brainee-module-card${hasGuide ? '' : ' is-disabled'}`}
                                             key={topic.topic_id ?? topic.id}
                                             type="button"
                                             disabled={!hasGuide}
                                             onClick={() => openGuide(course, topic)}
                                          >
                                             <span className="brainee-module-card-icon">
                                                {hasGuide ? <AntDIcons.FilePdfOutlined /> : <AntDIcons.FileUnknownOutlined />}
                                             </span>
                                             <span className="brainee-module-card-copy">
                                                <span className="brainee-module-card-title">{topic.title || 'Untitled topic'}</span>
                                                <span className="brainee-module-card-subtitle">
                                                   {hasGuide ? 'Open teacher guide' : 'No guide uploaded'}
                                                </span>
                                             </span>
                                             {hasGuide ? <AntDIcons.EyeOutlined className="brainee-module-card-arrow" /> : null}
                                          </button>
                                       );
                                    })}
                                 </div>
                              ) : <Empty description="No topics in this course." />}
                           </Panel>
                        );
                     })}
                  </Collapse>
               ) : (
                  <div className="brainee-module-state">
                     <Empty description={search ? 'No matching courses or topics.' : 'No courses found.'} />
                  </div>
               )}
            </section>

            <Modal
               title={preview ? `${preview.courseName} — ${preview.title}` : "Teacher's Guide"}
               centered
               destroyOnClose
               open={Boolean(preview)}
               width="80%"
               footer={<Button shape="round" onClick={() => setPreview(null)}>Close</Button>}
               onCancel={() => setPreview(null)}
            >
               {preview ? (
                  <div className="modal-document-viewer">
                     <DocumentViewer document={preview.document} documentname={preview.name} dvheight="calc(100dvh - 220px)" ismodal />
                  </div>
               ) : null}
            </Modal>
         </div>
      </div>
   );
}
